import React, { FC, useState, useEffect } from 'react';
import { Grid, Box, Button, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { pages } from '../../../../utils/common';
import { useUsers } from '../contexts/users.context';
import MainTitle from '../../../components/mainTitle';
import UsersHeader from './UsersHeader';
import UsersFilters from './UsersFilters';
import TabGroup from '../../../components/tabGroup';
import { TabItem } from '../../../components/components.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import AddEditGuardianUserView from '../subviews/manage/addEditVisitorUser.view';
import AddEditParentUserView from '../subviews/manage/addEditParentUser.view';
import AddEditGuestUserView from '../subviews/manage/addEditGuestUser.view';
import AddEditInternalUserView from '../subviews/manage/addEditInternalUser.view';
import CategoryUserDetailsView from '../subviews/details/categoryUserDetails.view';
import CategoryUsersView from '../subviews/categoryUsers.view';
import { useUsersByCategoryHook } from '../hooks/useUsersByCategoryHook';
import { UserCategory } from '../users.interfaces';
import { CATEGORY_DISPLAY_LABELS } from '../users.helpers';
import { UserByCategoryItem } from '../../../../apis/users/users.api.interfaces';

enum EUsersView {
  LIST = 'list',
  DETAILS = 'details',
}

const CATEGORY_ORDER: UserCategory[] = [
  UserCategory.VISITOR,
  UserCategory.GUARDIAN,
  UserCategory.STAFF,
  UserCategory.EXTERNAL,
  UserCategory.STUDENT,
];

interface CategoryTabPanelProps {
  category: UserCategory;
  search: string;
  onUserSelect: (user: UserByCategoryItem) => void;
  selectedUser: any;
  currentCategory: UserCategory;
  viewType: EUsersView;
  onBack: () => void;
  onFilterChange: (key: string, value: any) => void;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onResetFilters: () => void;
}

const CategoryTabPanel: FC<CategoryTabPanelProps> = ({
  category,
  search,
  onUserSelect,
  selectedUser,
  currentCategory,
  viewType,
  onBack,
  onFilterChange,
  onSearchChange,
  onResetFilters,
}) => {
  const { translateText } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { data, isLoading } = useUsersByCategoryHook(category, page, limit, search || undefined);
  const users = data?.data ?? [];
  const total = data?.total ?? 0;

  const showDetail =
    viewType === EUsersView.DETAILS &&
    selectedUser &&
    currentCategory === category;

  return (
    <Box
      sx={{ p: { xs: 1, sm: 2 }, height: '100%', overflow: 'auto' }}
      data-testid={`${category}-tab-panel`}
    >
      {showDetail ? (
        <Box data-testid={`${category}-user-details-container`}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: { xs: 1, sm: 2 }, gap: { xs: 1, sm: 2 } }}
            data-testid={`${category}-user-detail-header`}
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                mr: { xs: 0, sm: 2 },
                minHeight: { xs: '44px', sm: 'auto' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="back-to-list-button"
            >
              {translateText('users.details.backToList')}
            </Button>
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              data-testid="detail-header-title"
            >
              {selectedUser.firstName} {selectedUser.lastName}
            </Typography>
          </Box>
          <CategoryUserDetailsView />
        </Box>
      ) : (
        <>
          <UsersFilters
            onFilterChange={onFilterChange}
            onSearchChange={onSearchChange}
            onResetFilters={onResetFilters}
            data-testid="users-filters-section"
          />
          <Box sx={{ mt: { xs: 2, sm: 3 } }} data-testid={`${category}-content-section`}>
            <CategoryUsersView
              users={users}
              isLoading={isLoading}
              onUserSelect={onUserSelect}
              total={total}
              page={page}
              pageSize={limit}
              onPageChange={(newPage, newPageSize) => {
                setPage(newPage);
                setLimit(newPageSize);
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const UsersContent: FC = () => {
  const { translateText } = useTranslation();
  const {
    state,
    setFilters,
    setSearchQuery,
    setSelectedUser,
    resetFilters,
    exportUsers,
    openAddParent,
    openAddVisitor,
    openAddGuest,
    openAddInternal,
    setCategory,
    setActiveTab,
  } = useUsers();

  const { selectedUser, currentCategory, activeTabIndex } = state;

  const navigate = useNavigate();
  const { usersCategory } = useParams<{ usersCategory?: string }>();

  const [viewType, setViewType] = useState<EUsersView>(EUsersView.LIST);

  useEffect(() => {
    if (selectedUser) {
      setViewType(EUsersView.DETAILS);
    } else {
      setViewType(EUsersView.LIST);
    }
  }, [selectedUser]);

  // Sync the selected tab/category from the URL slug. When the slug is missing
  // or invalid, fall back to the first category and reflect it in the URL.
  useEffect(() => {
    const slugIndex = usersCategory
      ? CATEGORY_ORDER.indexOf(usersCategory as UserCategory)
      : -1;

    if (slugIndex === -1) {
      navigate(`${pages.users.path}/${CATEGORY_ORDER[0]}`, { replace: true });
      return;
    }

    if (slugIndex !== activeTabIndex) {
      setActiveTab(slugIndex);
      setCategory(CATEGORY_ORDER[slugIndex]);
      setSelectedUser(null);
      setViewType(EUsersView.LIST);
    }
  }, [usersCategory, activeTabIndex, navigate, setActiveTab, setCategory, setSelectedUser]);

  const handleExport = async (format: 'pdf' | 'excel') => {
    await exportUsers(format);
  };

  const handleUserSelect = (user: UserByCategoryItem) => {
    setSelectedUser(user as any);
    setViewType(EUsersView.DETAILS);
  };

  const handleBackToList = () => {
    setViewType(EUsersView.LIST);
    setSelectedUser(null);
  };

  const handleAddUser = () => {
    switch (currentCategory) {
      case UserCategory.GUARDIAN:
        openAddParent();
        break;
      case UserCategory.VISITOR:
        openAddVisitor();
        break;
      case UserCategory.EXTERNAL:
        openAddGuest();
        break;
      default:
        openAddInternal();
    }
  };

  const handleTabChange = (value: string | number) => {
    const idx = value as number;
    const nextCategory = CATEGORY_ORDER[idx] ?? UserCategory.VISITOR;
    // Drive the change through the URL; the slug-sync effect updates the
    // active tab/category in state.
    navigate(`${pages.users.path}/${nextCategory}`, { replace: true });
  };

  const handleFilterChange = (filterKey: string, value: any) => {
    setFilters({ [filterKey]: value });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const tabs: TabItem[] = CATEGORY_ORDER.map((category) => ({
    label: CATEGORY_DISPLAY_LABELS[category],
    tabPanel: (
      <CategoryTabPanel
        key={category}
        category={category}
        search={state.filters.searchQuery}
        onUserSelect={handleUserSelect}
        selectedUser={selectedUser}
        currentCategory={currentCategory}
        viewType={viewType}
        onBack={handleBackToList}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onResetFilters={resetFilters}
      />
    ),
  }));

  const selectedTabLabel = tabs[activeTabIndex]?.label ?? '';

  return (
    <Grid
      container
      id="usersViewContainer"
      sx={{ height: '100%' }}
      data-testid="users-container"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'stretch', sm: 'space-between' },
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 0,
          px: { xs: 0, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
        data-testid="users-header-section"
      >
        <MainTitle
          title={translateText('users.title')}
          subTitle={selectedTabLabel}
          data-testid="users-main-title"
          endAdornment={<UsersHeader onExport={handleExport} onAddUser={handleAddUser} />}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          px: { xs: 0, sm: 3 },
          flex: 1,
          height: 'calc(100% - 64px)',
          overflow: 'hidden',
        }}
        data-testid="users-tabs-section"
      >
        <TabGroup
          tabs={tabs}
          dataTestIdPrefix="users"
          initiallySelected={activeTabIndex}
          onTabChange={handleTabChange}
        />
      </Grid>

      <AddEditGuardianUserView />
      <AddEditParentUserView />
      <AddEditGuestUserView />
      <AddEditInternalUserView />
    </Grid>
  );
};

export default UsersContent;
